import { useEffect, useMemo, useState } from "react";

import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { environmentSelector } from "Reducers/environment";
import { environmentServicesSelector } from "Reducers/metrics/services.selector";
import getMetrics from "src/organization/pages/project/environment/metrics/api/getMetrics";
import { getMetricQuery } from "src/organization/pages/project/environment/metrics/http/helper/index";
import parseDimensions from "src/organization/pages/project/environment/metrics/utils/parseDimensions";
import { Poller } from "src/organization/pages/project/environment/metrics/utils/polling/poller";
import { MetricsPollType } from "src/organization/pages/project/environment/metrics/utils/polling/type";
import { useAppSelector } from "Store/hooks";

import { GRID_QUERY } from "../settings";
import { ResourceType } from "../types";

import { parseGridResource } from "./grid.utils";

const usePollGridResouces = ({
  timeframe,
  onChange,
  onError,
  onLoadingChange,
  interval = 30
}: MetricsPollType<ResourceType[]>) => {
  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();
  const environment = useSelectorWithUrlParams(environmentSelector);
  const [resource, setResource] = useState<ResourceType[]>();
  const betterServices = useAppSelector(state =>
    environmentServicesSelector(state, organizationId, projectId, environmentId)
  );

  const data = useMemo(() => {
    const data = parseGridResource(resource, betterServices);
    return data;
  }, [resource, betterServices]);

  const metricsLink = environment?.getLinks()?.["#metrics"]?.[0] as
    | {
        collection?: string;
        href: string;
      }
    | undefined;

  const poller = useMemo(
    () =>
      new Poller<ResourceType[]>({
        onChange: resource => {
          onChange(resource || []);
          setResource(resource);
          onError?.(undefined);
        },
        onError: err => {
          onError?.(err);
          onChange();
        },
        onLoadingChange
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (metricsLink) {
      const collection = metricsLink.collection;
      const href = metricsLink.href;

      const fetchMetrics = () => {
        const query = getMetricQuery({
          collection,
          timeframe,
          fields: GRID_QUERY
        });
        return getMetrics<ResourceType>(href, query);
      };
      timeframe.id
        ? poller.startPolling(fetchMetrics, interval)
        : poller.pollOnce(fetchMetrics);
    }
  }, [metricsLink, timeframe, interval, poller]);

  const servicesInstance = useMemo(() => {
    const v = (resource || []).reduce((acc, resource) => {
      const dimension =
        resource.point && resource.point.dimension
          ? parseDimensions(resource.point.dimension)
          : null;
      if (!dimension?.instance) return acc;

      const service = dimension.service;
      if (!acc[service]) {
        acc[service] = [];
      }
      acc[service].push(dimension.instance);

      return acc;
    }, {});

    const result = {};
    for (const name in v) {
      const instance = Array.from(new Set(v[name]));
      result[name] = instance.length > 1 ? [...instance, "average"] : instance;
    }

    return result;
  }, [resource]);

  useEffect(() => {
    return () => poller.stopPolling();
  }, [poller]);

  return { servicesInstance, data };
};

export default usePollGridResouces;
